import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ADMIN } from '@/constants/testIds';
import { Activity, ArrowUpRight, Filter, TrendingUp, Users } from 'lucide-react';

const RANGES = [
  { days: 7, label: '7D' },
  { days: 30, label: '30D' },
  { days: 90, label: '90D' },
];

export const AdminAttributionPage = () => {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) { setBusy(false); return; }
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get('/admin/attribution', { params: { days } });
        if (!cancelled) setData(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [user, days]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-2xl mx-auto mt-16 px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-yellow-400 mb-3">// ACCESS_DENIED</div>
        <h1 className="text-3xl font-bold uppercase tracking-tighter mb-6">
          403<span className="caelix-caret"></span>
        </h1>
        <div data-testid={ADMIN.forbidden} className="c-card">
          <p className="text-xs text-gray-500 mb-4 leading-relaxed">
            &gt; This surface is admin-only. If you should have access, ask your ops lead to add your email to <code className="text-[#38BDF8]">ADMIN_EMAILS</code>.
          </p>
          <Link to="/pro/manage" className="c-btn-primary text-xs">BACK_TO_MANAGE</Link>
        </div>
      </div>
    );
  }

  const totals = [
    { label: 'PAGE_VIEWS', value: data?.total_page_views ?? 0, icon: Activity, testId: ADMIN.totalPageViews, accent: false },
    { label: 'UNIQUE_VISITORS', value: data?.total_unique_visitors ?? 0, icon: Users, testId: ADMIN.totalVisitors, accent: false },
    { label: 'SIGNUPS', value: data?.total_signups ?? 0, icon: ArrowUpRight, testId: ADMIN.totalSignups, accent: true },
    { label: 'PAID', value: data?.total_paid ?? 0, icon: TrendingUp, testId: ADMIN.totalPaid, accent: true },
  ];

  return (
    <div data-testid={ADMIN.container} className="max-w-6xl mx-auto px-6 md:px-8 pt-10 pb-24">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">// ADMIN_ATTRIBUTION · FUNNEL</div>
      <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-6">
        Attribution<span className="caelix-caret"></span>
      </h1>
      <p className="text-xs text-gray-500 mb-6 max-w-2xl">
        &gt; First-party funnel analytics · no third-party trackers · IPs stored only as salted SHA-256 hashes.
      </p>

      {/* Range filter */}
      <div className="mb-6 flex items-center gap-2 flex-wrap">
        <Filter className="w-3 h-3 text-gray-500" strokeWidth={2.5} />
        <span className="text-[10px] tracking-[0.25em] uppercase text-gray-500">RANGE</span>
        {RANGES.map((r) => (
          <button
            key={r.days}
            data-testid={`${ADMIN.rangeBtn}-${r.days}`}
            onClick={() => setDays(r.days)}
            className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border transition-colors ${
              days === r.days
                ? 'border-[#38BDF8] text-[#38BDF8] bg-[#38BDF8]/[0.06]'
                : 'border-[#3F3F46] text-gray-500 hover:border-[#38BDF8]/40 hover:text-white'
            }`}
          >{r.label}</button>
        ))}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-6">
        {totals.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="c-card">
              <div className="flex items-center justify-between mb-2">
                <div className="c-label">{t.label}</div>
                <Icon className="w-3 h-3 text-[#38BDF8]" strokeWidth={2.5} />
              </div>
              <div
                data-testid={t.testId}
                className={`text-3xl md:text-4xl font-bold ${t.accent ? 'text-[#38BDF8]' : 'text-white'}`}
              >
                {String(t.value).padStart(2, '0')}
              </div>
            </div>
          );
        })}
      </div>

      {/* By source */}
      {busy ? (
        <div className="c-card text-xs text-gray-500 tracking-widest uppercase">
          LOADING<span className="caelix-dots"></span>
        </div>
      ) : !data || data.by_source.length === 0 ? (
        <div data-testid={ADMIN.emptyState} className="c-card">
          <div className="text-xs text-gray-500 tracking-widest uppercase">&gt; no events yet · beacons will start populating once public pages are visited</div>
        </div>
      ) : (
        <div className="c-card">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 border-b border-[#3F3F46] pb-3 mb-4">// BY_SOURCE</div>
          <div className="grid grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr] gap-3 text-[10px] tracking-[0.2em] uppercase text-gray-500 py-2 border-b border-[#3F3F46]">
            <div>SOURCE</div>
            <div>VIEWS</div>
            <div>UNIQUE</div>
            <div>SIGNUPS</div>
            <div>PAID</div>
            <div>SIGN_RATE</div>
            <div>PAID_RATE</div>
          </div>
          {data.by_source.map((s) => (
            <div
              key={s.source}
              data-testid={ADMIN.sourceRow}
              className="grid grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr] gap-3 items-center py-3 border-b border-[#27272A] last:border-0 text-xs hover:bg-[#27272A] transition-colors"
            >
              <div className="font-mono text-white truncate">{s.source}</div>
              <div className="font-mono text-white">{s.page_views}</div>
              <div className="font-mono text-white">{s.unique_visitors}</div>
              <div className="font-mono text-[#38BDF8]">{s.signups}</div>
              <div className="font-mono text-[#38BDF8]">{s.paid}</div>
              <div className="font-mono text-gray-400">{(s.signup_rate * 100).toFixed(1)}%</div>
              <div className="font-mono text-gray-400">{(s.paid_rate * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 text-[10px] text-gray-600 tracking-widest uppercase">
        &gt; window {days}d · generated {data?.generated_at?.slice(0, 19).replace('T', ' ') || '—'}
      </div>
    </div>
  );
};

export default AdminAttributionPage;
